import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Checkbox,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Container
} from '@mui/material';
import {
  EmojiEvents as TrophyIcon,
  People as PeopleIcon,
  Publish as PublishIcon,
  Preview as PreviewIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { adminResultsService } from '../services/adminResultsService';
import { eventsService } from '../services/api';

const EventResultsPage = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [saving, setSaving] = useState(false);
  
  // Data states
  const [event, setEvent] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [selectedWinner, setSelectedWinner] = useState('');
  const [selectedFinalists, setSelectedFinalists] = useState([]);
  const [currentResults, setCurrentResults] = useState(null);
  
  // UI states
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    console.log('🚀 EventResultsPage mounted with eventId:', eventId);
    if (eventId) {
      loadData();
    } else {
      console.log('❌ No eventId provided');
    }
  }, [eventId]);

  const loadData = async () => {
    console.log('🔍 Starting loadData for eventId:', eventId);
    try {
      setLoading(true);
      setError('');
      
      console.log('📡 Making single API call to get participants with stories...');
      // Only make one call - the participants endpoint has all the data we need
      const participantsResponse = await adminResultsService.getParticipantsWithStories(eventId);
      
      console.log('✅ API call completed');
      console.log('Participants Response:', participantsResponse);
      
      // Get event data from the participants response
      const eventData = participantsResponse?.data?.event;
      if (!eventData) {
        setError('Event data not found.');
        setLoading(false);
        return;
      }
      
      setEvent(eventData);
      console.log('Event data:', eventData);
      console.log('Event status:', eventData?.status);
      
      // Check if event is closed
      if (eventData?.status !== 'closed') {
        console.log('❌ Event is not closed:', eventData?.status);
        setError('Results can only be managed for closed events.');
        setLoading(false);
        return;
      }
      
      // Handle participants data
      const participantsList = participantsResponse?.data?.participants || [];
      console.log('Participants List:', participantsList);
      console.log('Participants List Length:', participantsList.length);
      setParticipants(participantsList);
      
      // Handle current results from the same response
      const currentResults = participantsResponse?.data?.current_results;
      console.log('Current Results:', currentResults);
      setCurrentResults(currentResults);
      
      // Set current selections if results exist
      if (currentResults?.winner) {
        const winnerKey = `${currentResults.winner.user_id}-${currentResults.winner.story_id}`;
        console.log('Setting winner key:', winnerKey);
        setSelectedWinner(winnerKey);
      }
      if (currentResults?.finalists) {
        const finalistKeys = currentResults.finalists.map(f => `${f.user_id}-${f.story_id}`);
        console.log('Setting finalist keys:', finalistKeys);
        setSelectedFinalists(finalistKeys);
      }
      
      console.log('✅ Data loading completed successfully');
      
    } catch (err) {
      console.error('❌ Error loading data:', err);
      setError('Failed to load event data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleWinnerChange = (event) => {
    setSelectedWinner(event.target.value);
  };

  const handleFinalistChange = (storyKey) => {
    setSelectedFinalists(prev => {
      if (prev.includes(storyKey)) {
        return prev.filter(key => key !== storyKey);
      } else {
        return [...prev, storyKey];
      }
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      
      // Save winner if selected
      if (selectedWinner) {
        // Split more carefully - story keys are in format: userId-storyId
        // But UUIDs have dashes, so we need to find the story- part
        const storyIndex = selectedWinner.indexOf('-story-');
        if (storyIndex === -1) {
          setError('Invalid winner selection format.');
          setSaving(false);
          return;
        }
        
        const userId = selectedWinner.substring(0, storyIndex);
        const storyId = selectedWinner.substring(storyIndex + 1); // Remove the leading dash
        
        console.log('Saving winner:', { userId, storyId });
        
        await adminResultsService.setWinner(eventId, {
          user_id: userId,
          story_id: storyId
        });
      }
      
      // Save finalists if selected
      if (selectedFinalists.length > 0) {
        const finalists = selectedFinalists.map((key, index) => {
          const storyIndex = key.indexOf('-story-');
          if (storyIndex === -1) {
            throw new Error('Invalid finalist selection format.');
          }
          
          const userId = key.substring(0, storyIndex);
          const storyId = key.substring(storyIndex + 1);
          
          return {
            user_id: userId,
            story_id: storyId,
            rank: index + 1
          };
        });
        
        console.log('Saving finalists:', finalists);
        
        await adminResultsService.setFinalists(eventId, { finalists });
      }
      
      setSuccess('Results saved successfully!');
      await loadData(); // Reload to get updated data
      
    } catch (err) {
      console.error('Error saving results:', err);
      setError('Failed to save results. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!selectedWinner) {
      setError('Please select a winner before publishing results.');
      return;
    }

    try {
      setSaving(true);
      setError('');
      
      await adminResultsService.publishResults(eventId);
      setSuccess('Results published successfully!');
      await loadData();
      
    } catch (err) {
      console.error('Error publishing results:', err);
      setError('Failed to publish results. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleUnpublish = async () => {
    try {
      setSaving(true);
      setError('');
      
      await adminResultsService.unpublishResults(eventId);
      setSuccess('Results unpublished successfully.');
      await loadData();
      
    } catch (err) {
      console.error('Error unpublishing results:', err);
      setError('Failed to unpublish results. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const getStoryKey = (participant, story) => {
    const key = `${participant.user_id}-${story.story_id}`;
    console.log('Generated story key:', key, 'for participant:', participant.user_id, 'story:', story.story_id);
    return key;
  };

  const getSelectedWinnerInfo = () => {
    if (!selectedWinner) return null;
    
    for (const participant of participants) {
      for (const story of participant.stories || []) {
        if (getStoryKey(participant, story) === selectedWinner) {
          return { participant, story };
        }
      }
    }
    return null;
  };

  const getSelectedFinalistsInfo = () => {
    const finalists = [];
    for (const key of selectedFinalists) {
      for (const participant of participants) {
        for (const story of participant.stories || []) {
          if (getStoryKey(participant, story) === key) {
            finalists.push({ participant, story });
            break;
          }
        }
      }
    }
    return finalists;
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!event) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">
          Event not found.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box mb={3}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/admin/events')}
          sx={{ mb: 2 }}
        >
          Back to Events
        </Button>
        
        <Typography variant="h4" gutterBottom>
          Manage Event Results
        </Typography>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          {event.title}
        </Typography>
        
        <Box display="flex" gap={1} mb={2}>
          <Chip 
            label={`${participants.length} Participants`} 
            icon={<PeopleIcon />}
            variant="outlined"
          />
          {currentResults?.winner && (
            <Chip 
              label="Winner Set" 
              icon={<TrophyIcon />}
              color="success"
            />
          )}
          {selectedFinalists.length > 0 && (
            <Chip 
              label={`${selectedFinalists.length} Finalists`}
              color="info"
            />
          )}
          {currentResults?.results_published && (
            <Chip 
              label="Published" 
              icon={<PublishIcon />}
              color="primary"
            />
          )}
        </Box>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {participants.length === 0 ? (
        <Alert severity="info">
          No participants with submitted stories found for this event.
        </Alert>
      ) : (
        <Grid container spacing={3}>
          {/* Left Column - Story Selection */}
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Select Winner and Finalists
              </Typography>
              
              {/* Winner Selection */}
              <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
                <FormLabel component="legend">Winner</FormLabel>
                <RadioGroup value={selectedWinner} onChange={handleWinnerChange}>
                  {participants.map((participant) =>
                    participant.stories?.map((story) => (
                      <FormControlLabel
                        key={getStoryKey(participant, story)}
                        value={getStoryKey(participant, story)}
                        control={<Radio />}
                        label={
                          <Box>
                            <Typography variant="body1" fontWeight="bold">
                              {story.story_title || 'Untitled Story'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              by {participant.display_name} • {story.word_count} words
                            </Typography>
                          </Box>
                        }
                      />
                    ))
                  )}
                </RadioGroup>
              </FormControl>

              <Divider sx={{ my: 3 }} />

              {/* Finalists Selection */}
              <FormControl component="fieldset" sx={{ width: '100%' }}>
                <FormLabel component="legend">Finalists (Optional)</FormLabel>
                <Box sx={{ mt: 1 }}>
                  {participants.map((participant) =>
                    participant.stories?.map((story) => {
                      const storyKey = getStoryKey(participant, story);
                      const isWinner = selectedWinner === storyKey;
                      
                      return (
                        <FormControlLabel
                          key={storyKey}
                          control={
                            <Checkbox
                              checked={selectedFinalists.includes(storyKey)}
                              onChange={() => handleFinalistChange(storyKey)}
                              disabled={isWinner}
                            />
                          }
                          label={
                            <Box>
                              <Typography 
                                variant="body1" 
                                fontWeight="bold"
                                color={isWinner ? 'text.disabled' : 'text.primary'}
                              >
                                {story.story_title || 'Untitled Story'}
                                {isWinner && ' (Winner)'}
                              </Typography>
                              <Typography 
                                variant="body2" 
                                color={isWinner ? 'text.disabled' : 'text.secondary'}
                              >
                                by {participant.display_name} • {story.word_count} words
                              </Typography>
                            </Box>
                          }
                        />
                      );
                    })
                  )}
                </Box>
              </FormControl>
            </Paper>
          </Grid>

          {/* Right Column - Preview and Actions */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Results Preview
              </Typography>
              
              {selectedWinner && (
                <Box mb={2}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    🏆 Winner
                  </Typography>
                  {(() => {
                    const winnerInfo = getSelectedWinnerInfo();
                    return winnerInfo ? (
                      <Card variant="outlined" sx={{ mb: 2 }}>
                        <CardContent sx={{ py: 1 }}>
                          <Typography variant="body2" fontWeight="bold">
                            {winnerInfo.story.story_title}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            by {winnerInfo.participant.display_name}
                          </Typography>
                        </CardContent>
                      </Card>
                    ) : null;
                  })()}
                </Box>
              )}

              {selectedFinalists.length > 0 && (
                <Box mb={2}>
                  <Typography variant="subtitle2" color="secondary" gutterBottom>
                    🥈 Finalists
                  </Typography>
                  {getSelectedFinalistsInfo().map((info, index) => (
                    <Card key={index} variant="outlined" sx={{ mb: 1 }}>
                      <CardContent sx={{ py: 1 }}>
                        <Typography variant="body2" fontWeight="bold">
                          {info.story.story_title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          by {info.participant.display_name}
                        </Typography>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              )}

              <Box display="flex" flexDirection="column" gap={2} mt={3}>
                <Button
                  variant="outlined"
                  onClick={() => setPreviewOpen(true)}
                  startIcon={<PreviewIcon />}
                  disabled={!selectedWinner}
                >
                  Preview Results
                </Button>
                
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={saving || !selectedWinner}
                  color="primary"
                >
                  {saving ? 'Saving...' : 'Save Results'}
                </Button>
                
                {currentResults?.results_published ? (
                  <Button
                    variant="outlined"
                    onClick={handleUnpublish}
                    disabled={saving}
                    color="warning"
                  >
                    {saving ? 'Unpublishing...' : 'Unpublish Results'}
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    onClick={handlePublish}
                    disabled={saving || !currentResults?.winner}
                    color="success"
                    startIcon={<PublishIcon />}
                  >
                    {saving ? 'Publishing...' : 'Publish Results'}
                  </Button>
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Results Preview</DialogTitle>
        <DialogContent>
          <Typography variant="h6" gutterBottom>
            {event.title} - Results
          </Typography>
          
          {(() => {
            const winnerInfo = getSelectedWinnerInfo();
            return winnerInfo ? (
              <Box mb={3}>
                <Typography variant="h6" color="primary" gutterBottom>
                  🏆 Winner
                </Typography>
                <Card>
                  <CardContent>
                    <Typography variant="h6">{winnerInfo.story.story_title}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      by {winnerInfo.participant.display_name}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {winnerInfo.story.word_count} words • {winnerInfo.story.view_count} views • {winnerInfo.story.comment_count} comments
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
            ) : null;
          })()}

          {selectedFinalists.length > 0 && (
            <Box>
              <Typography variant="h6" color="secondary" gutterBottom>
                🥈 Finalists
              </Typography>
              {getSelectedFinalistsInfo().map((info, index) => (
                <Card key={index} sx={{ mb: 1 }}>
                  <CardContent>
                    <Typography variant="subtitle1">{info.story.story_title}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      by {info.participant.display_name}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {info.story.word_count} words • {info.story.view_count} views • {info.story.comment_count} comments
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default EventResultsPage;
